import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { VENUE_TOUCH_ICON_180_URL } from '@/lib/brandMark';
import { installMetaFor } from '@/lib/installRoutes';

/**
 * Owns the install definition for the internal screens.
 *
 * Mounted once at the app root so it can react to route changes: a shortcut's
 * launch URL is fixed at the moment it is saved, so each internal screen has to
 * carry the right definition while it is open — not only when it first mounts.
 * Public routes are left completely untouched, so the storefront keeps its
 * installable, branded manifest exactly as it is today.
 */
export default function InstallMetaManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const config = installMetaFor(pathname);
    if (!config) return;

    const injected = [];

    // Any manifest already in the head belongs to a different screen. Detach it
    // while this screen is open so it can't dictate where the saved shortcut
    // launches, and put it back on the way out.
    const displaced = Array.from(document.querySelectorAll('link[rel="manifest"]'));
    displaced.forEach((el) => el.remove());

    // A screen with no manifest of its own gets none attached: with nothing
    // present Safari falls back to the page's own URL, which is the deep link
    // we want for the back office and the live screens.
    if (config.manifest) {
      const manifest = document.createElement('link');
      manifest.setAttribute('rel', 'manifest');
      manifest.setAttribute('href', config.manifest);
      document.head.appendChild(manifest);
      injected.push(manifest);
    }

    const touchIcon = document.createElement('link');
    touchIcon.setAttribute('rel', 'apple-touch-icon');
    touchIcon.setAttribute('sizes', '180x180');
    touchIcon.setAttribute('href', VENUE_TOUCH_ICON_180_URL);
    document.head.appendChild(touchIcon);
    injected.push(touchIcon);

    // Keeps a saved internal shortcut opening full-screen standalone, under the
    // name of the screen it was saved from.
    [
      ['apple-mobile-web-app-capable', 'yes'],
      ['apple-mobile-web-app-status-bar-style', 'black-translucent'],
      ['apple-mobile-web-app-title', config.title],
      ['theme-color', '#0D0D0D'],
    ].forEach(([name, content]) => {
      const el = document.createElement('meta');
      el.setAttribute('name', name);
      el.setAttribute('content', content);
      document.head.appendChild(el);
      injected.push(el);
    });

    return () => {
      injected.forEach((el) => el.remove());
      displaced.forEach((el) => document.head.appendChild(el));
    };
  }, [pathname]);

  return null;
}